import usePDFTheme from '@/utils/hooks/usePDFTheme';
import { Image, StyleSheet, Text, View } from '@react-pdf/renderer';
import { useMemo } from 'react';
import { PortfolioStatementProps } from './PortfolioStatement';

const PortfolioStatementPositionsTable = ({ metadata, positions }: PortfolioStatementProps) => {
    const maxPct = positions.reduce((max, position) => Math.max(max, position.allocation), 0);
    const pdfTheme = usePDFTheme();

    const { layout, palette, fontFamily } = pdfTheme;

    const styles = useMemo(() => {
        return StyleSheet.create({
            card: {
                backgroundColor: palette.surface,
                borderRadius: 12,
                borderWidth: 1,
                borderColor: palette.border,
                overflow: 'hidden'
            },
            headRow: {
                flexDirection: 'row',
                alignItems: 'center',
                backgroundColor: palette.primaryWash,
                borderBottomWidth: 1,
                borderBottomColor: palette.border,
                paddingVertical: 10,
                paddingHorizontal: 16,
                borderTopLeftRadius: 11,
                borderTopRightRadius: 11
            },
            headCell: {
                fontSize: 6.8,
                letterSpacing: 1.2,
                color: palette.primaryDeep,
                fontFamily,
                fontWeight: 600
            },
            row: {
                flexDirection: 'row',
                alignItems: 'center',
                borderBottomWidth: 1,
                borderBottomColor: palette.border,
                paddingVertical: 9,
                paddingHorizontal: 16
            },
            lastRow: {
                borderBottomWidth: 0
            },
            assetCell: {
                flexDirection: 'row',
                alignItems: 'center'
            },
            badge: {
                width: 20,
                height: 20,
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: 9
            },
            badgeText: {
                fontSize: 8,
                color: palette.primaryDeep,
                fontFamily,
                fontWeight: 600
            },
            ticker: {
                fontSize: 9.5,
                color: palette.ink,
                fontFamily,
                fontWeight: 600
            },
            assetName: {
                fontSize: 6.8,
                marginTop: 1,
                color: palette.neutral,
                fontFamily,
                fontWeight: 400
            },
            numeric: {
                fontSize: 9,
                textAlign: 'right',
                color: palette.ink,
                fontFamily,
                fontWeight: 400
            },
            value: {
                fontSize: 9.5,
                textAlign: 'right',
                color: palette.ink,
                fontFamily,
                fontWeight: 600
            },
            allocationCell: {
                alignItems: 'flex-end',
                paddingLeft: layout.allocationGutter
            },
            allocationText: {
                fontSize: 9,
                color: palette.ink,
                fontFamily,
                fontWeight: 600
            },
            track: {
                height: 3,
                width: '100%',
                marginTop: 5,
                borderRadius: 2,
                backgroundColor: palette.primaryWash
            },
            fill: {
                height: 3,
                borderRadius: 2,
                backgroundColor: palette.primary
            },
            currencyIcon: {
                width: 20,
                height: 20,
                borderRadius: 100,
                overflow: 'hidden'
            }
        });
    }, [fontFamily, palette, layout]);

    return (
        <View style={styles.card}>
            <View style={styles.headRow}>
                <Text style={[styles.headCell, { width: layout.columns.asset }]}>ASSET</Text>
                <Text style={[styles.headCell, { width: layout.columns.total, textAlign: 'right' }]}>TOTAL</Text>
                <Text style={[styles.headCell, { width: layout.columns.rate, textAlign: 'right' }]}>
                    RATE ({metadata.currency})
                </Text>
                <Text style={[styles.headCell, { width: layout.columns.value, textAlign: 'right' }]}>
                    LIVE VALUE ({metadata.currency})
                </Text>
                <Text
                    style={[
                        styles.headCell,
                        {
                            width: layout.columns.allocation,
                            textAlign: 'right',
                            paddingLeft: layout.allocationGutter
                        }
                    ]}>
                    ALLOCATION
                </Text>
            </View>

            {positions
                .sort((a, b) => b.allocation - a.allocation)
                .map((position, index) => {
                    return (
                        <PortfolioPosition
                            key={position.asset}
                            styles={styles}
                            maxPct={maxPct}
                            layout={layout}
                            position={position}
                            isLast={index === positions.length - 1}
                        />
                    );
                })}
        </View>
    );
};

export default PortfolioStatementPositionsTable;

const PortfolioPosition = ({
    styles,
    position,
    layout,
    isLast,
    maxPct
}: {
    styles: any;
    position: any;
    layout: any;
    isLast: boolean;
    maxPct: number;
}) => {
    const width = maxPct > 0 ? `${Math.max((position.allocation / maxPct) * 100, 0)}%` : '0%';

    return (
        <View key={position.asset} style={isLast ? [styles.row, styles.lastRow] : styles.row} wrap={false}>
            <View style={[styles.assetCell, { width: layout.columns.asset }]}>
                <View style={styles.badge}>
                    <Image
                        style={styles.currencyIcon}
                        src={`/currency_icons/svg/color/${position.asset.toLowerCase()}.svg`}
                    />
                </View>
                <View>
                    <Text style={styles.ticker}>{position.asset}</Text>
                    <Text style={styles.assetName}>{position.asset}</Text>
                </View>
            </View>

            <Text style={[styles.numeric, { width: layout.columns.total }]}>{position.total}</Text>
            <Text style={[styles.numeric, { width: layout.columns.rate }]}>{position.rate}</Text>
            <Text style={[styles.value, { width: layout.columns.value }]}>{position.live_value}</Text>

            <View style={[styles.allocationCell, { width: layout.columns.allocation }]}>
                <Text style={styles.allocationText}>{`${(position.allocation * 100).toFixed(2)}%`}</Text>
                <View style={styles.track}>
                    <View style={[styles.fill, { width }]} />
                </View>
            </View>
        </View>
    );
};
